import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { ShortCard, ShortEmbed, ShortActions, useWeeklyShorts } from './ShortsHighlights';

const ShortModal = ({ short, onClose }) => (
  <Dialog open={!!short} onOpenChange={(open) => !open && onClose()}>
    <DialogContent
      className="max-w-[95vw] max-h-[95vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 bg-card border-gold/40 p-4 gap-3"
      style={{ width: 'max(340px, calc(min(55vh, 600px) * 0.5625 + 2rem))' }}
      data-testid="home-short-modal"
    >
      {short && (
        <>
          <div className="aspect-[9/16] mx-auto bg-black rounded-sm overflow-hidden border border-gold/40 mt-6" style={{ height: 'min(55vh, 600px)' }}>
            <ShortEmbed short={short} testId="home-short-iframe" />
          </div>
          <DialogTitle className="font-cinzel text-base text-foreground leading-snug line-clamp-2" data-testid="home-short-modal-title">
            {short.title}
          </DialogTitle>
          <DialogDescription className="sr-only">Playing a True Believer YouTube Short</DialogDescription>
          <ShortActions short={short} testPrefix="home-short" className="!flex-nowrap !gap-2 [&>a]:flex-1 [&>a]:whitespace-nowrap [&>a]:justify-center [&>a]:text-[11px] [&>a]:px-2 [&>a]:py-2" />
        </>
      )}
    </DialogContent>
  </Dialog>
);

export const HomeShortsStrip = () => {
  const { shorts, status } = useWeeklyShorts();
  const [selected, setSelected] = useState(null);

  if (status === 'error') return null;

  return (
    <section className="py-20" data-testid="home-shorts-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-10"
        >
          <div>
            <p className="text-deep-red uppercase tracking-[0.3em] text-xs mb-3">Fresh Every Sunday</p>
            <h2 className="font-cinzel text-3xl sm:text-4xl text-foreground" data-testid="home-shorts-title">
              This Week's <span className="text-gold">Highlights</span>
            </h2>
          </div>
          <Link to="/media" className="btn-secondary inline-flex items-center gap-2 self-start sm:self-auto" data-testid="home-shorts-see-all">
            See All Highlights <ArrowRight size={16} />
          </Link>
        </motion.div>

        {status === 'loading' ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="home-shorts-loading">
            {[0, 1, 2, 3].map((i) => <div key={i} className="aspect-[9/16] bg-muted animate-pulse rounded-sm" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="home-shorts-grid">
            {shorts.map((s, i) => <ShortCard key={s.video_id} short={s} index={i} onSelect={setSelected} testPrefix="home-short" />)}
          </div>
        )}
      </div>
      <ShortModal short={selected} onClose={() => setSelected(null)} />
    </section>
  );
};
